/**
 * Agent Builder — list built-in + my agents; create, edit, view, duplicate, delete, launch.
 * Launch opens the Workspace (Ask Adrsta) with the agent selected.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Eye, Pencil, Play, Plus, Search, Trash2 } from "lucide-react";
import {
  createAgent, deleteAgent, duplicateAgent, listAgents, updateAgent,
  type Agent, type AgentInput,
} from "./api";
import AgentFormModal from "./components/AgentFormModal";
import AgentIcon from "./components/AgentIcon";

type ModalState = { mode: "create" } | { mode: "edit"; agent: Agent } | { mode: "view"; agent: Agent } | null;

export default function AgentBuilderPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<ModalState>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      setAgents(await listAgents());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load agents.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => `${a.name} ${a.description}`.toLowerCase().includes(q));
  }, [agents, query]);

  const builtins = filtered.filter((a) => a.isBuiltin);
  const mine = filtered.filter((a) => !a.isBuiltin);

  const handleSubmit = async (data: AgentInput) => {
    if (modal?.mode === "edit") await updateAgent(modal.agent.id, data);
    else await createAgent(data);
    await refresh();
  };

  const handleDuplicate = async (agent: Agent) => {
    try {
      const copy = await duplicateAgent(agent.id);
      await refresh();
      setModal({ mode: "edit", agent: copy });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not duplicate the agent.");
    }
  };

  const handleDelete = async (agent: Agent) => {
    if (!window.confirm(`Delete "${agent.name}"? This cannot be undone.`)) return;
    try {
      await deleteAgent(agent.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the agent.");
    }
  };

  const renderCard = (agent: Agent) => {
    return (
      <div key={agent.id} className="flex flex-col rounded-card border border-border-default-t bg-surface p-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-adrsta-blue"
            style={{ background: "var(--blue-subtle)" }}
          >
            <AgentIcon icon={agent.icon} className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-[14px] font-bold text-primary-t">{agent.name}</h3>
              {agent.isBuiltin && (
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-adrsta-blue"
                  style={{ background: "var(--blue-subtle)" }}
                >
                  Built-in
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-tertiary-t">
              {agent.isBuiltin ? "Read-only" : `Updated ${new Date(agent.updatedAt).toLocaleDateString()}`}
            </p>
          </div>
        </div>
        <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-[13px] text-secondary-t">
          {agent.description || "No description"}
        </p>
        <div className="mt-4 flex items-center gap-2 border-t border-border-subtle-t pt-4">
          <button
            onClick={() => navigate(`/workspace?agent=${agent.id}`)}
            className="flex items-center gap-1.5 rounded-lg bg-adrsta-blue px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
          >
            <Play className="h-4 w-4" /> Launch
          </button>
          <div className="ml-auto flex gap-1">
            {agent.isBuiltin ? (
              <IconButton title="View" onClick={() => setModal({ mode: "view", agent })} icon={Eye} />
            ) : (
              <IconButton title="Edit" onClick={() => setModal({ mode: "edit", agent })} icon={Pencil} />
            )}
            <IconButton title="Duplicate" onClick={() => handleDuplicate(agent)} icon={Copy} />
            {!agent.isBuiltin && <IconButton title="Delete" onClick={() => handleDelete(agent)} icon={Trash2} danger />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-7">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-primary-t">Agent Builder</h1>
          <p className="mt-1 text-[13px] text-secondary-t">Launch a built-in agent or build your own marketing agent.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-tertiary-t" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents"
              className="w-56 rounded-lg border border-border-default-t bg-surface py-2 pl-9 pr-3 text-[13px] text-primary-t placeholder:text-tertiary-t focus:border-adrsta-blue focus:outline-none"
            />
          </div>
          <button
            onClick={() => setModal({ mode: "create" })}
            className="flex items-center gap-2 rounded-lg bg-adrsta-blue px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Create agent
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-6 rounded-lg px-4 py-3 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
          {error}
        </p>
      )}

      {loading ? (
        <p className="mt-10 text-[13px] text-tertiary-t">Loading agents…</p>
      ) : (
        <>
          <section className="mt-8">
            <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-tertiary-t">My agents</h2>
            {mine.length === 0 ? (
              <div className="rounded-card border border-dashed border-border-default-t bg-surface px-6 py-10 text-center">
                <p className="text-[13px] text-secondary-t">
                  {query ? "No agents match your search." : "You haven't created any agents yet."}
                </p>
                {!query && (
                  <button
                    onClick={() => setModal({ mode: "create" })}
                    className="mt-3 text-[13px] font-semibold text-adrsta-blue hover:underline"
                  >
                    Create your first agent
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{mine.map(renderCard)}</div>
            )}
          </section>

          {builtins.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-tertiary-t">Built-in agents</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{builtins.map(renderCard)}</div>
            </section>
          )}
        </>
      )}

      <AgentFormModal
        open={modal !== null}
        agent={modal && modal.mode !== "create" ? modal.agent : null}
        readOnly={modal?.mode === "view"}
        onClose={() => setModal(null)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

function IconButton({
  title, onClick, icon: Icon, danger,
}: {
  title: string;
  onClick: () => void;
  icon: typeof Eye;
  danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`rounded-lg p-2 text-tertiary-t hover:bg-hover-bg ${danger ? "hover:text-[color:var(--error)]" : "hover:text-adrsta-blue"}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
